/* ----------------- React --------------- */
import { useEffect, useState, useTransition } from "react";

/* ----------------- Constants --------------- */
import { ENDPOINT_HINT } from "@constants/urls";

/* ----------------- Features --------------- */
import useSessionStore from "./useSessionStore";

import i18n from "@helpers/lib/i18n";
/* ----------------- Helpers --------------- */
import type { BaseItemSchema } from "@memorang/types/src/session/new-session";

import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { useAppStore } from "@hooks/useAppStore";
import { getPlatform } from "@memorang/helpers/src/common";
import type { Hint } from "@memorang/types/src/session/common";
/* ----------------- React Query --------------- */
import { useMutation } from "@tanstack/react-query";
import { fetch as expoFetch } from "expo/fetch";
import { v4 as uuidv4 } from "uuid";
import { getParentStem } from "./useAIFeedback";

export const useHint = ({
	item,
	type = "item",
	selectedChoice,
	selectedChoiceId,
}: {
	item: BaseItemSchema;
	type?: "item" | "choice";
	selectedChoice?: string;
	selectedChoiceId?: string;
}) => {
	const { id: itemId, metadata, content, tags } = item;

	const choices = item.response.type === "choice" ? item.response.choices : [];
	const stem = content?.stem?.text;
	const topics = metadata?.topics || tags?.map((tag) => tag.label);

	const appId = useAppStore((store) => store.app.id);

	const parentStem = getParentStem(item);

	const {
		currentExamContext: { examId },
	} = useExamContext();

	const [hints, setHints] = useState<Hint[]>([]);
	const correctChoice = choices?.find((choice) => choice.isCorrect);
	const explanation = correctChoice ? correctChoice.explanation : "";
	const incorrectChoices = choices
		?.filter((choice) => !choice.isCorrect)
		.map((choice) => choice.text);

	const correctChoiceText = correctChoice?.text;
	const { viewerId: userId } = useAuthContext();

	const platform = getPlatform();
	const [loading, setLoading] = useState(false);
	const [streaming, setStreaming] = useState(false);

	const [pending, startTransition] = useTransition();
	const updateHintsForItem = useSessionStore(
		(state) => state.updateHintsForItem,
	);

	const { mutate: getHint } = useMutation({
		mutationKey: selectedChoiceId
			? ["hint", itemId, selectedChoiceId]
			: ["hint", itemId],
		mutationFn: async () => {
			const params = {
				platform,
				itemId: typeof itemId === "string" ? itemId : itemId?.toString(),
				examId: `${examId}`,
				originalQuestion: {
					topics,
					explanation,
					stem,
					incorrectChoices,
					correctChoice: correctChoiceText,
					selectedChoice,
				},
				type,
				userId,
				appId,
				outputLanguage: i18n.language,
				extraDetails: parentStem,
			};
			setLoading(true);
			const response = await expoFetch(ENDPOINT_HINT, {
				method: "POST",
				headers: {
					Accept: "text/event-stream",
				},
				body: JSON.stringify(params),
			});
			const reader = response.body?.getReader();
			return reader;
		},
		onSuccess: (reader) => {
			setLoading(false);
			setStreaming(true);
			setHints((prevHints) => [
				...prevHints,
				{
					id: uuidv4(),
					text: "",
				},
			]);
			const decoder = new TextDecoder("utf-8");
			let result = "";

			async function read() {
				if (reader) {
					const { done, value } = await reader.read();
					if (done) {
						setStreaming(false);
						return;
					}
					result += decoder.decode(value);
					startTransition(() => {
						setHints((prevHint) => {
							prevHint[prevHint.length - 1].text = result;
							return prevHint;
						});
					});
					await read();
				}
			}
			read();
		},
	});

	useEffect(() => {
		if (hints.length > 0 && !loading && !streaming && !pending) {
			updateHintsForItem(itemId!, hints);
		}
	}, [hints, loading, streaming, itemId, pending, updateHintsForItem]);

	return {
		hints,
		loading,
		streaming,
		getHint,
	};
};

export type UseHintReturnType = ReturnType<typeof useHint>;
